'use client';

import { useEffect, useRef } from 'react';

// Web Speech API типы (DOM lib не везде их объявляет — описываем минимум сами).
type SRAlternative = { transcript: string };
type SRResult = { isFinal: boolean; 0: SRAlternative; length: number };
type SRResultList = { length: number; [index: number]: SRResult };
type SRResultEvent = { resultIndex: number; results: SRResultList };
type SRErrorEvent = { error?: string };

type SpeechRecognitionInstance = {
	continuous: boolean;
	interimResults: boolean;
	lang: string;
	onresult: ((e: SRResultEvent) => void) | null;
	onerror: ((e: SRErrorEvent) => void) | null;
	onend: (() => void) | null;
	start: () => void;
	stop: () => void;
	abort: () => void;
};

type WindowWithSpeech = typeof window & {
	SpeechRecognition?: { new (): SpeechRecognitionInstance };
	webkitSpeechRecognition?: { new (): SpeechRecognitionInstance };
};

type UseSpeechRecognitionOpts = {
	/** Управляющий флаг — true начинает распознавание, false его останавливает. */
	active: boolean;
	/** Локаль распознавания. По умолчанию ru-RU. */
	lang?: string;
	/** Вызывается на каждом финальном фрагменте — текст уже триммирован. */
	onFinal: (text: string) => void;
	/** Опционально — ошибка распознавания (нет поддержки, отказ в правах и т.п.). */
	onError?: (message: string) => void;
};

/**
 * Тонкая обёртка над `SpeechRecognition` / `webkitSpeechRecognition`.
 * Хук сам стартует/останавливает распознавание по `active`, прокидывая только
 * финальные сегменты речи наружу.
 */
export function useSpeechRecognition({
	active,
	lang = 'ru-RU',
	onFinal,
	onError,
}: UseSpeechRecognitionOpts) {
	const onFinalRef = useRef(onFinal);
	const onErrorRef = useRef(onError);
	onFinalRef.current = onFinal;
	onErrorRef.current = onError;

	useEffect(() => {
		if (!active) return;
		if (typeof window === 'undefined') return;

		const w = window as WindowWithSpeech;
		const Ctor = w.SpeechRecognition || w.webkitSpeechRecognition;
		if (!Ctor) {
			onErrorRef.current?.('Голосовой ввод не поддерживается в этом браузере');
			return;
		}

		const recognition = new Ctor();
		recognition.continuous = true;
		recognition.interimResults = false;
		recognition.lang = lang;

		recognition.onresult = (e) => {
			for (let i = e.resultIndex; i < e.results.length; i++) {
				const r = e.results[i];
				if (r.isFinal) {
					const text = (r[0]?.transcript ?? '').trim();
					if (text) onFinalRef.current(text);
				}
			}
		};

		recognition.onerror = (e) => {
			const code = e.error ?? 'unknown';
			if (code === 'no-speech' || code === 'aborted') return;
			onErrorRef.current?.(`Ошибка распознавания: ${code}`);
		};

		// continuous=true в Chrome всё равно иногда сам останавливается через таймаут
		// или после паузы — перезапускаем, пока пользователь не остановил вручную.
		let stoppedByUs = false;
		recognition.onend = () => {
			if (stoppedByUs) return;
			try {
				recognition.start();
			} catch {
				// no-op: уже остановлен
			}
		};

		try {
			recognition.start();
		} catch (e) {
			onErrorRef.current?.(
				e instanceof Error ? e.message : 'Не удалось запустить распознавание',
			);
		}

		return () => {
			stoppedByUs = true;
			try {
				recognition.stop();
			} catch {
				// no-op
			}
		};
	}, [active, lang]);
}
